// React
import * as React from "react";
import ReactPlayer from "react-player";
import moment from "moment";
// Amplify UI
import { Card, Collection, Flex, Text, useTheme } from "@aws-amplify/ui-react";
import { liveChannelService, mediaService } from "../../services";
import { EventCard } from "../event-card";
import { ILiveStream, IMemberPlans } from "../../types";
import { useScrollLock } from "../hooks";
import * as Dialog from "@radix-ui/react-dialog";
import * as Styled from "./LiveEventsCollection.styled";

const NoResultsFound: React.FC = () => {
    const { tokens } = useTheme();
    return (
        <Flex justifyContent="flex-start" padding={tokens.space.large}>
            <Text fontSize="16px">
                There are no events currently streaming. Please check back later
                for updates.
            </Text>
        </Flex>
    );
};

export const LiveEventsCollection: React.FC<{
    events: ILiveStream[];
    sessionToken: string;
    isSignedIn: boolean;
}> = ({ events, sessionToken, isSignedIn }) => {
    const { tokens } = useTheme();
    const [showPlayerModal, setShowPlayerModal] = React.useState(false);
    useScrollLock(showPlayerModal);
    const [currentPlayingMediaItem, setCurrentPlayingMediaItem] =
        React.useState<any>(undefined);
    const [activePlans, setActivePlans] = React.useState<IMemberPlans[]>([]);
    const [hasFullAccess, setHasFullAccess] = React.useState<boolean>(false);
    const [freeTrialPlanIds, setFreeTrialPlanIds] = React.useState<string[]>([]);

    const fetchCustomerActivePlans = React.useCallback(async () => {
        const result = await liveChannelService.getCustomerPurchasedPlans(
            sessionToken!
        );
        setActivePlans(
            result.filter((item) =>
                item.expiryDate
                    ? new Date(item.expiryDate).getTime() > Date.now()
                    : true
            )
        );

        const trialInfo = await mediaService.checkTrialCodeUser(
            sessionToken!
        );
        if (trialInfo.length > 0) {
            setFreeTrialPlanIds(trialInfo[0].planId);
            setHasFullAccess(trialInfo[0].isFullAccess);
        }
    }, [sessionToken]);

    React.useEffect(() => {
        window.addEventListener("onFetchUserPlansEvent", () => {
            fetchCustomerActivePlans();
        });
    }, [fetchCustomerActivePlans]);

    React.useEffect(() => {
        if (isSignedIn && sessionToken) {
            fetchCustomerActivePlans();
        }
    }, [fetchCustomerActivePlans, isSignedIn, sessionToken]);

    const generateLoadSignInModalEvent = React.useCallback(
        (event: ILiveStream, showPurchasePlanModal?: boolean) => {
            const displayLoginEvent = new CustomEvent("loadSignInModal", {
                detail: {
                    eventRequested: event,
                    showPurchasePlanModal: showPurchasePlanModal,
                    requestedPlanType: 2,
                },
            });

            window.dispatchEvent(displayLoginEvent);
        },
        []
    );

    const handleWatchClick = React.useCallback(
        async (event: ILiveStream) => {
            if (event.isPrivate) {
                console.log("isPrivate", event.isPrivate);
                if (isSignedIn) {
                    console.log("isSignedIn", isSignedIn);
                    const hasAccess = !!event.planDetails.length
                        ? !!activePlans.filter((memberPlan) =>
                            event.planDetails.some((eventPlan) => {
                                return eventPlan?.id === memberPlan.id;
                            })
                        ).length ||
                        freeTrialPlanIds.includes(event.id) ||
                        hasFullAccess
                        : true;
                    console.log("hasAccess", hasAccess);
                    if (hasAccess) {

                        if (
                            moment(
                                moment(event.eventStartDate, moment.ISO_8601)
                            ).isBefore(
                                moment(new Date(), moment.ISO_8601),
                                "day"
                            ) || event.streamingUrl
                        ) {
                            try {
                                const token =
                                    await liveChannelService.getPlaybackToken(
                                        event.id,
                                        sessionToken
                                    );
                                setShowPlayerModal(true);

                                const eventWithToken = {
                                    ...event,
                                    streamingUrl: `${event.streamingUrl}?token=${token}`,
                                };
                                setCurrentPlayingMediaItem(eventWithToken);
                            } catch (err) {
                                console.log(
                                    "Error while fetching token is: ",
                                    err
                                );
                            }
                        } else {
                            console.log("Event has not started yet.");
                        }
                    } else {
                        generateLoadSignInModalEvent(event, true);
                        console.log("Does not have access to this event.");
                    }
                } else {
                    generateLoadSignInModalEvent(event);
                    console.log("Not signed in.");
                }
            } else {
                // check event start dat/time
                if (
                    moment(
                        moment(event.eventStartDate, moment.ISO_8601)
                    ).isBefore(moment(new Date(), moment.ISO_8601), "day")
                ) {
                    setShowPlayerModal(true);
                    setCurrentPlayingMediaItem(event);
                } else if (event.streamingUrl) {
                    setShowPlayerModal(true);
                    setCurrentPlayingMediaItem(event);
                } else {
                    console.log("Event has not started yet.");
                }
            }
        },
        [
            activePlans,
            isSignedIn,
            sessionToken,
            hasFullAccess,
            freeTrialPlanIds,
            generateLoadSignInModalEvent,
        ]
    );

    return (
        <>
            <Collection
                items={events}
                type="list"
                direction={"row"}
                wrap={"wrap"}
                gap="20px"
                padding={tokens.space.large}
                isPaginated={events.length > 0}
                itemsPerPage={6}
                searchNoResultsFound={<NoResultsFound />}
            >
                {(item, index) => {
                    return (
                        <Card
                            key={index}
                            borderRadius="light"
                            variation="elevated"
                            height="256px"
                            width="352px"
                            padding="0"
                            className="event-card live-stream"
                        >
                            <EventCard
                                key={index}
                                event={item}
                                isSignedIn={isSignedIn}
                                hasEligiblePlan={
                                    isSignedIn &&
                                    (!!item.planDetails.length
                                        ? !!activePlans.filter((memberPlan) =>
                                            item.planDetails.some(
                                                (itemPlan) =>
                                                    itemPlan?.id ===
                                                    memberPlan.id
                                            )
                                        ).length ||
                                        freeTrialPlanIds.includes(item.id) ||
                                        hasFullAccess
                                        : true)
                                }
                                onWatchClick={handleWatchClick}
                            />
                        </Card>
                    );
                }}
            </Collection>

            <Dialog.Root
                open={showPlayerModal}
                onOpenChange={(open: boolean) => !open && setShowPlayerModal(false)}
                modal={false}
            >
                <Dialog.Portal>
                    <Styled.DialogOverlay data-cemboo="live-events-modal-overlay" />
                    <Styled.DialogContent data-cemboo="live-events-modal-content">
                        <Styled.ModalHeader data-cemboo="live-events-modal-header">
                            <Styled.ModalTitle data-cemboo="live-events-modal-title">
                                {currentPlayingMediaItem?.title}
                            </Styled.ModalTitle>
                            <Styled.CloseButton data-cemboo="live-events-modal-close" aria-label="Close">
                                ×
                            </Styled.CloseButton>
                        </Styled.ModalHeader>
                        <Styled.ModalBody data-cemboo="live-events-modal-body">
                            <ReactPlayer
                                url={currentPlayingMediaItem?.streamingUrl}
                                width={"100%"}
                                height={"100%"}
                                controls
                            />
                        </Styled.ModalBody>
                    </Styled.DialogContent>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    );
};
